import * as React from 'react';
import {
  DesignServices,
  DriveFileRenameOutline,
  ArrowCircleDown,
  Visibility,
} from '@mui/icons-material';
import { type ApolloQueryResult } from '@apollo/react-hooks';
import { type CustomAction } from './types';
import { COMMON_ACTION } from './constants';
import { type ApplicationViewTableDataItem } from './graphql';
import { IconButton } from '../../../../components';
import { handleViewAsApplicant } from '../../../../utils';
import { type OneOffDialogConfig, OneOffDialog } from '../../../Assessment';

export type ApplicationViewTableActionCellProps = {
  rowData: ApplicationViewTableDataItem;
  customActions?: CustomAction[];
  pickCommonActions?: COMMON_ACTION[];
  refetch: () => Promise<ApolloQueryResult<any>>;
};

export const ApplicationViewTableActionCell: React.FunctionComponent<
  ApplicationViewTableActionCellProps
> = props => {
  const {
    customActions = [],
    rowData,
    pickCommonActions = [
      COMMON_ACTION.MONITOR,
      COMMON_ACTION.VIEW_AS,
      COMMON_ACTION.ONE_OFF,
    ],
    refetch,
  } = props;
  const { applicationId, applicationNo, applicationStatus } = rowData;
  const [oneOffDialogConfig, setOpenOneOffDialog] =
    React.useState<OneOffDialogConfig>({
      open: false,
      applicationId: '',
      applicationNo: '',
    });

  const handleViewMonitor = (): void => {
    if (applicationId) window.open(`/internal/monitor/${applicationId}`);
  };

  const handleDownloadProfile = (): void => {
    setOpenOneOffDialog({
      open: true,
      applicationId,
      applicationNo,
    });
  };
  const { open: openDialog } = oneOffDialogConfig;

  const COMMON_ACTION_CONFIG = {
    [COMMON_ACTION.MONITOR]: {
      handler: handleViewMonitor,
      title: 'Application Profile',
      icon: <Visibility />,
    },
    [COMMON_ACTION.VIEW_AS]: {
      handler: handleViewAsApplicant(applicationId, applicationStatus),
      title: 'View as Applicant',
      icon: <DesignServices />,
    },
    [COMMON_ACTION.ONE_OFF]: {
      handler: handleDownloadProfile,
      title: 'Download Profile',
      icon: <ArrowCircleDown />,
    },
  } as const;

  return (
    <div className="flex justify-center items-center">
      {openDialog && (
        <OneOffDialog
          openDialogConfig={oneOffDialogConfig}
          onDialogClose={() => {
            setOpenOneOffDialog({
              open: false,
              applicationId: '',
              applicationNo: '',
            });
          }}
        />
      )}

      <div className="flex flex-wrap justify-center items-center max-w-[100px]">
        {customActions.map((customAction, index) => {
          const { checkDisable, handleAction, icon, title } = customAction;
          const handleEditApplication = (): void => {
            handleAction(rowData, refetch);
          };
          const disabled = checkDisable ? checkDisable(rowData) : true;
          const resolvedIcon = icon ? (
            typeof icon === 'function' ? (
              icon(rowData)
            ) : (
              icon
            )
          ) : (
            <DriveFileRenameOutline />
          );
          return (
            <IconButton
              key={`customs-action-${index}`}
              title={title}
              onClick={handleEditApplication}
              disabled={disabled}
              color={!disabled ? 'primary' : 'inherit'}
              icon={resolvedIcon}
            />
          );
        })}

        {Object.entries(COMMON_ACTION_CONFIG).map(([action, config]) => {
          const actionNumber = Number(action);
          return (
            pickCommonActions.includes(actionNumber) && (
              <IconButton
                key={actionNumber}
                onClick={config.handler}
                title={config.title}
                color="primary"
                icon={config.icon}
              />
            )
          );
        })}
      </div>
    </div>
  );
};
